/**
 * The ONE source of the website forms' default consent checkboxes.
 *
 * Moved out of the configurator's `themes.ts` (which re-exports it) so the
 * builder sections (DataInput, Contact, CheckboxesGroup, the preview tile),
 * the 14 page templates, the create wizard and the AI wizard all seed the
 * same rows. `themes.ts` pulls in
 * `lzutf8` and every event template, so the type below is a local alias on
 * purpose — importing `TranslationFn` from `temp.ts` would drag the whole
 * template graph into every importer's chunk.
 *
 * GDPR Art. 7(4) (Kopplungsverbot): a REQUIRED row must never store into
 * `emailOptIn`. Ticking a mandatory box is not a free choice, so binding it
 * to the marketing opt-in forced `emailOptIn = true` for every guest who
 * registered. The required row is a pure acknowledgement (no field — the
 * runtime validates unlinked checkboxes fine); the opt-in is optional and
 * starts unticked. `decoupleConsent` is the guard every write path uses.
 */

type Translate = (key: string) => string;

/** The standard guest field the marketing opt-in writes. */
export const EMAIL_OPT_IN_FIELD = "emailOptIn";

/** One consent checkbox of the form. `fieldVariable` links it to a BOOLEAN
 *  guest field or the standard "emailOptIn"; absent = stores nothing. */
export type RegistrationConsent = {
  text: string;
  required: boolean;
  fieldVariable?: string;
};

/** The Checkboxes node's row shape (the builder's own prop name). */
export type ConsentCheckboxItem = {
  text: string;
  required: boolean;
  inputFieldVariable?: string;
};

/** True for the forbidden pairing: a mandatory row bound to the opt-in. */
export function isCoupledOptIn(row: {
  required?: boolean;
  inputFieldVariable?: string;
}): boolean {
  return row.required === true && row.inputFieldVariable === EMAIL_OPT_IN_FIELD;
}

/**
 * The organiser's privacy URL (WebsiteConfig.privacyPolicyUrl) if it is a
 * usable ABSOLUTE http(s) URL without credentials, else undefined — the same
 * rule the published legal line applies (website-builder-frontend
 * runtime/components/legalLine.tsx `legalHref`), so the checkbox never links
 * what the legal line would refuse.
 */
export function organiserPrivacyUrl(candidate: unknown): string | undefined {
  if (typeof candidate !== "string") return undefined;
  const trimmed = candidate.trim();
  if (trimmed === "" || trimmed.length > 2048) return undefined;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return undefined;
    if (parsed.username !== "" || parsed.password !== "") return undefined;
    return parsed.toString();
  } catch {
    return undefined;
  }
}

const escapeAttr = (v: string) =>
  v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * `sentence` with its `phrase` turned into a link to the organiser's privacy
 * notice — the markup the checkbox text editor's own link tool writes (new
 * tab, noopener). Audit 7.12 M7: the guest ticks "I have read the organiser's
 * privacy notice", so the notice must be one click away. The published
 * runtime renders the row text as sanitised HTML and resolves no
 * placeholders, so the link is baked in when the row is seeded; no usable URL
 * ⇒ the plain sentence (the Checkboxes panel then asks for the URL). Each
 * locale's phrase is an exact substring of its sentence (pinned per locale by
 * registrationConsents.privacyLink.test.ts); should one ever drift, the link
 * is appended rather than lost.
 */
function linkPrivacyPhrase(sentence: string, phrase: string, url: string | undefined): string {
  const href = organiserPrivacyUrl(url);
  if (!href || !phrase) return sentence;
  const anchor = `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${phrase}</a>`;
  const at = sentence.indexOf(phrase);
  if (at < 0) return `${sentence} (${anchor})`;
  return sentence.slice(0, at) + anchor + sentence.slice(at + phrase.length);
}

/**
 * The registration form's starting rows: the required acknowledgement of the
 * organiser's privacy notice (stores nothing) + the OPTIONAL, unticked
 * marketing opt-in — the only row writing `emailOptIn`. Terms and conditions
 * are no longer a default; organisers add them as a row of their own.
 * `privacyPolicyUrl`: the website's privacy URL when known at insertion time
 * (sections inserted into an existing site); the acknowledgement links it.
 */
export function defaultRegistrationConsents(
  td: Translate,
  privacyPolicyUrl?: string,
): RegistrationConsent[] {
  return [
    {
      text: linkPrivacyPhrase(td("privacyNoticeOrganiser"), td("privacyNoticeOrganiserLink"), privacyPolicyUrl),
      required: true,
    },
    { text: td("emailOptInConsent"), required: false, fieldVariable: EMAIL_OPT_IN_FIELD },
  ];
}

/** The contact form's single default row: a required privacy-notice
 *  acknowledgement that stores nothing on the guest (linked like above). */
export function defaultContactConsents(td: Translate, privacyPolicyUrl?: string): RegistrationConsent[] {
  return [
    { text: linkPrivacyPhrase(td("privacyPolicy"), td("privacyPolicyLink"), privacyPolicyUrl), required: true },
  ];
}

/** The standalone CheckboxesGroup's default row: a required terms consent of
 *  its own — no field, so ticking it never writes emailOptIn. */
export function defaultCheckboxesGroupConsents(td: Translate): RegistrationConsent[] {
  return [{ text: td("termsAndConditions"), required: true }];
}

/**
 * Wizard/template shape → Checkboxes node rows (`fieldVariable` →
 * `inputFieldVariable`). An empty variable leaves the key OFF (not
 * `undefined`), so serialised documents stay byte-identical to the
 * hand-written template rows. A required row bound to `emailOptIn` keeps
 * its required flag and loses the binding — never the other way round.
 */
export function toCheckboxItems(consents: readonly RegistrationConsent[]): ConsentCheckboxItem[] {
  return consents.map((c) => {
    const item: ConsentCheckboxItem = { text: c.text, required: c.required };
    if (c.fieldVariable && !(c.required && c.fieldVariable === EMAIL_OPT_IN_FIELD)) {
      item.inputFieldVariable = c.fieldVariable;
    }
    return item;
  });
}
